import { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { FBSendEvents, GASendEvent, GAFBSendEvent } from 'utils/statistics'
import { toWithoutSpaces } from 'utils/convertors'
import { toNumberWithDecimal, toWithSeparator } from 'utils/getters'

import CalculatorForm from './CalculatorForm/CalculatorFormContainer'
import BarChartTable from './BarChartTable/BarChartTable'
import styles from './FeesCalculator.module.scss'

const isNumber = value => {
  // Check for [0-9] and allow only on dot (.)
  if (`${value}`.length && (typeof value === 'number' || typeof value === 'string'))
    return /^[0-9,.]*$/g.test(`${value}`) && !/.*\..*\..*/g.test(`${value}`)

  return false
}

// If in fees we have: (2$ = 2$ / month) else (2 = 2% / year)
const getFees = fees => {
  let feesSwitcher = '%'
  let feesPerYear = 1

  if (`${fees}`.includes('$')) {
    const feesNumber = fees.replace('$', '')
    feesSwitcher = '$'
    feesPerYear = isNumber(feesNumber) ? Number(feesNumber) * 12 : 1 * 12
  } else {
    feesPerYear = isNumber(fees) ? Number(fees) : 1
  }

  return { feesSwitcher, feesPerYear }
}

const withContributions = (value, contributions, frequency) => {
  switch (frequency) {
    case 'Weekly':
      return toNumberWithDecimal(value + contributions * 52)

    case 'Fortnightly':
      return toNumberWithDecimal(value + contributions * 26)

    case 'Monthly':
      return toNumberWithDecimal(value + contributions * 12)

    case 'Annually':
      return toNumberWithDecimal(value + contributions)

    default:
      return 0
  }
}

const FeesCalculator = ({ fees: investmentFees, termCalculator, minInvestment, target }) => {
  const { feesSwitcher, feesPerYear } = getFees(investmentFees)
  const defaultForm = useMemo(
    () => ({
      investmentAmount: minInvestment > 1000 ? toNumberWithDecimal(minInvestment) : 1000,
      investmentAmountView: minInvestment > 1000 ? toWithSeparator(minInvestment) : '1,000',
      targetReturn: isNumber(target.min) ? +target.min : 8,
      targetReturnView: isNumber(target.min) ? `${target.min}` : '8',
      term: termCalculator || 5,
      termView: termCalculator ? `${termCalculator}` : '5',
      fees: feesPerYear,
      feesView: `${feesPerYear}`,
      feesSwitcher,
      contributions: 100,
      contributionsView: '100',
      frequency: 'Monthly'
    }),
    [feesPerYear, feesSwitcher, minInvestment, target.min, termCalculator]
  )

  const [form, setForm] = useState(defaultForm)

  // Calculate results based on form data
  const handleCalcResults = useCallback(() => {
    const { investmentAmount, targetReturn, term, fees, feesSwitcher: newFeesSwitcher, contributions, frequency } = form

    // Total Fees and Return after Fees -  P * ((1 + r / 100) ^ t)
    return [...Array(Math.trunc(term))].reduce(
      ({ amountInvested, totalFees, valueAfterFees, chart }, _, index) => {
        const valueWithContributions = withContributions(valueAfterFees, contributions, frequency)
        const yearContributions = withContributions(0, contributions, frequency)
        const profit = toNumberWithDecimal(valueWithContributions * (1 + targetReturn / 100) ** 1)
        const fee = newFeesSwitcher === '%' ? toNumberWithDecimal((profit / 100) * fees) : fees // in $
        const net = toNumberWithDecimal(profit - fee)
        const updateFee = toNumberWithDecimal(totalFees + fee)

        const currentChartData = {
          name: `${index + 1}y`,
          start: valueWithContributions,
          startVisual: toNumberWithDecimal(valueWithContributions * 0.2), // trick for 'Beginning of year' to hack the BarChatr
          fees: fee,
          return: toNumberWithDecimal(net - valueWithContributions)
        }

        return {
          amountInvested: amountInvested + yearContributions,
          totalFees: updateFee,
          valueAfterFees: net,
          chart: [...chart, currentChartData]
        }
      },
      { amountInvested: investmentAmount, totalFees: 0, valueAfterFees: investmentAmount, chart: [] }
    )
  }, [form])

  // Store form results
  const [formResults, setFormResults] = useState(handleCalcResults())

  // Recalc form results on form value chage
  useEffect(() => {
    setFormResults(handleCalcResults())
  }, [form, handleCalcResults])

  // To handle similar platform changes
  useEffect(() => {
    setForm(defaultForm)
  }, [defaultForm])

  const handleSetFormPureValue = (name, value) => setForm({ ...form, [name]: value || form[name] })

  // Test to pass only numbers and add to the state if number
  const handleSetFormValue = ({ target: { name, value } }) => {
    const clearValue = toWithoutSpaces(value)
    const newValue = clearValue.length > 0 ? (isNumber(clearValue) ? clearValue : form[name]) : ''
    const newFormObject = { ...form, [name]: toNumberWithDecimal(newValue), [`${name}View`]: toWithSeparator(newValue) }
    const { investmentAmount, targetReturn, term, fees, feesSwitcher: newFeesSwitcher } = newFormObject

    // Trackers
    GAFBSendEvent('FeesCalcSubmit', 'platform', 'FeesCalculator')

    FBSendEvents('FeesCalcData', newFormObject)

    GASendEvent('FeesCalcSubmit', 'FeesCalculator')
    GASendEvent('FeesCalcData_investment_amount', investmentAmount)
    GASendEvent('FeesCalcData_target_return', targetReturn)
    GASendEvent('FeesCalcData_term', term)
    GASendEvent('FeesCalcData_fees', fees)
    GASendEvent('FeesCalcData_switcher', newFeesSwitcher)

    setForm(newFormObject)
  }

  return (
    <div className={styles.FeesCalculator}>
      <CalculatorForm
        form={form}
        handleSetFormPureValue={handleSetFormPureValue}
        handleSetFormValue={handleSetFormValue}
      />
      <BarChartTable form={form} formResults={formResults} />
    </div>
  )
}

FeesCalculator.propTypes = {
  fees: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  minInvestment: PropTypes.number,
  termCalculator: PropTypes.number,
  target: PropTypes.shape({
    min: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    max: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
}

FeesCalculator.defaultProps = {
  fees: 1,
  minInvestment: 1000,
  target: {
    min: 8
  },
  termCalculator: 0
}

export default FeesCalculator
